import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resetAntiforgery } from './client';
import type {
  AdminGuess,
  AuthMeResponse,
  AuthRequestResponse,
  AuthVerifyResponse,
  CreateSweepstakeRequest,
  ExitImpersonationResponse,
  FeaturesResponse,
  LeaderboardResponse,
  MarkPaidRequest,
  PublicSweepstake,
  ResolvedSweepstakeResponse,
  SubmittedGuessDto,
  SweepstakeAdminDto,
  SweepstakeStatus,
  SweepstakeSummary,
  UpdateSweepstakeRequest,
} from './types';

export const queryKeys = {
  me: ['auth', 'me'] as const,
  features: ['features'] as const,
  publicSweepstake: (slug: string) => ['public', 'sweepstake', slug] as const,
  publicLeaderboard: (slug: string) => ['public', 'leaderboard', slug] as const,
  mine: ['sweepstakes', 'mine'] as const,
  admin: (id: string) => ['sweepstakes', 'admin', id] as const,
  adminGuesses: (id: string) => ['sweepstakes', 'admin', id, 'guesses'] as const,
  adminInfoBySlug: (slug: string) => ['sweepstakes', 'admin-info', slug] as const,
};

/**
 * Feature-flag manifest. Backed by an anonymous endpoint with a 30s Cache-Control so the
 * SPA can hide surfaces (e.g. the JG page URL input on create) without round-tripping the
 * server every render. staleTime mirrors the server's max-age — flips reach the UI within
 * one cache cycle.
 */
export function useFeatures() {
  return useQuery({
    queryKey: queryKeys.features,
    queryFn: () => api<FeaturesResponse>('/api/features'),
    staleTime: 30_000,
    retry: false,
  });
}

export function useAuthMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api<AuthMeResponse>('/api/auth/me'),
    retry: false,
    // Keep the previous successful response visible during transient errors so the
    // impersonation banner doesn't disappear on a brief network outage.
    placeholderData: keepPreviousData,
  });
}

export function useRequestSignIn() {
  return useMutation({
    mutationFn: (email: string) =>
      api<AuthRequestResponse>('/api/auth/request', { method: 'POST', body: { email } }),
  });
}

export function useVerifySignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { email: string; code?: string; token?: string }) =>
      api<AuthVerifyResponse>('/api/auth/verify', { method: 'POST', body: vars }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.me, data);
      // Antiforgery tokens are bound to the user identity. The cached token (likely fetched
      // for the POST /api/auth/request call while anonymous) won't match the new signed-in
      // identity — clear it so the next mutating call refetches a token tied to the
      // post-sign-in user. Logout + exit-impersonation do the same.
      resetAntiforgery();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(queryKeys.me, null);
      resetAntiforgery();
    },
  });
}

// Defence-in-depth check on the server-returned redirect target. We expect an admin host
// on the guessmytime.com eTLD+1 — either prod (admin.guessmytime.com) or test
// (admin.test.guessmytime.com), or any future `admin-*` sibling. Anything else means the
// server returned an unexpected URL and we refuse to navigate.
export function isAllowedAdminRedirect(redirectTo: string): boolean {
  let url: URL;
  try {
    url = new URL(redirectTo);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (!url.hostname.endsWith('.guessmytime.com')) return false;
  const leftmost = url.hostname.split('.')[0];
  return leftmost === 'admin' || leftmost.startsWith('admin-');
}

export function useExitImpersonation() {
  return useMutation({
    mutationFn: () => api<ExitImpersonationResponse>('/api/auth/exit-impersonation', { method: 'POST' }),
    onSuccess: (data) => {
      if (!isAllowedAdminRedirect(data.redirectTo)) {
        throw new Error(`exit-impersonation returned an unexpected redirect target: ${data.redirectTo}`);
      }
      // The cookie was just invalidated; the cached antiforgery token was bound to it.
      resetAntiforgery();
      window.location.assign(data.redirectTo);
    },
  });
}

export function usePublicSweepstake(slug: string) {
  return useQuery({
    queryKey: queryKeys.publicSweepstake(slug),
    queryFn: () => api<PublicSweepstake>(`/api/public/sweepstakes/${encodeURIComponent(slug)}`),
    retry: false,
  });
}

/**
 * Owner-only admin-URL lookup by public slug. The endpoint returns 404 for non-owners
 * and unauthenticated callers, so the query is gated on `enabled` to avoid a guaranteed
 * 404 round-trip for everyone who isn't signed in. When it resolves with data, the
 * caller is the organiser and can be offered the "Edit this sweepstake" link.
 */
export function useAdminInfoBySlug(slug: string, signedIn: boolean) {
  return useQuery({
    queryKey: queryKeys.adminInfoBySlug(slug),
    queryFn: () => api<{ id: string; adminUrl: string }>(
      `/api/sweepstakes/by-slug/${encodeURIComponent(slug)}/admin-info`,
    ),
    enabled: signedIn && slug.length > 0,
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicLeaderboard(slug: string, status: SweepstakeStatus | undefined) {
  const refetchInterval = status === 'Open' ? 10_000 : false;
  return useQuery({
    queryKey: queryKeys.publicLeaderboard(slug),
    queryFn: () => api<LeaderboardResponse>(`/api/public/sweepstakes/${encodeURIComponent(slug)}/guesses`),
    refetchInterval,
    retry: false,
  });
}

export function useSubmitGuess(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; email?: string; hours: number; minutes: number; seconds: number }) =>
      api<SubmittedGuessDto>(`/api/public/sweepstakes/${encodeURIComponent(slug)}/guesses`, {
        method: 'POST',
        body: vars,
      }),
    onSuccess: (created) => {
      qc.setQueryData<LeaderboardResponse | undefined>(queryKeys.publicLeaderboard(slug), (prev) => {
        const newItem = {
          id: created.id,
          name: created.name,
          guessSeconds: created.guessSeconds,
          submittedAt: created.submittedAt,
          softDuplicate: created.softDuplicate,
        };
        if (!prev) {
          return { items: [newItem], nextCursor: null };
        }
        return { ...prev, items: [...prev.items, newItem] };
      });
    },
  });
}

export function useMySweepstakes() {
  return useQuery({
    queryKey: queryKeys.mine,
    queryFn: () => api<SweepstakeSummary[]>('/api/sweepstakes/mine'),
  });
}

export function useAdminSweepstake(id: string) {
  return useQuery({
    queryKey: queryKeys.admin(id),
    queryFn: () => api<SweepstakeAdminDto>(`/api/sweepstakes/${id}/admin`),
    retry: false,
  });
}

export function useCreateSweepstake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSweepstakeRequest) =>
      api<SweepstakeAdminDto>('/api/sweepstakes', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.mine });
    },
  });
}

export function useUpdateSweepstake(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSweepstakeRequest) =>
      api<SweepstakeAdminDto>(`/api/sweepstakes/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.admin(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.publicSweepstake(data.slug) });
      qc.invalidateQueries({ queryKey: queryKeys.mine });
    },
  });
}

export function useTransitionStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: SweepstakeStatus) =>
      api<SweepstakeAdminDto>(`/api/sweepstakes/${id}/status`, {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.admin(id), data);
      qc.invalidateQueries({ queryKey: queryKeys.mine });
      qc.invalidateQueries({ queryKey: queryKeys.publicSweepstake(data.slug) });
    },
  });
}

export function useAdminGuesses(id: string) {
  return useQuery({
    queryKey: queryKeys.adminGuesses(id),
    queryFn: () => api<AdminGuess[]>(`/api/sweepstakes/${id}/guesses/admin`),
    retry: false,
  });
}

export function useSetPaymentTracking(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api<SweepstakeAdminDto>(`/api/sweepstakes/${id}/payment-tracking`, {
        method: 'PATCH',
        body: { enabled },
      }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.admin(id), data);
    },
  });
}

export function useMarkGuessPaid(sweepstakeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { guessId: string; body: MarkPaidRequest }) =>
      api<AdminGuess>(`/api/sweepstakes/${sweepstakeId}/guesses/${vars.guessId}`, {
        method: 'PATCH',
        body: vars.body,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<AdminGuess[] | undefined>(queryKeys.adminGuesses(sweepstakeId), (prev) => {
        if (!prev) return prev;
        return prev.map((g) => (g.id === updated.id ? { ...updated, softDuplicate: g.softDuplicate } : g));
      });
    },
  });
}

export function useResolveSweepstake(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { hours: number; minutes: number; seconds: number }) =>
      api<ResolvedSweepstakeResponse>(`/api/sweepstakes/${id}/resolve`, {
        method: 'POST',
        body: vars,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.admin(id) });
      qc.invalidateQueries({ queryKey: queryKeys.mine });
      qc.invalidateQueries({ queryKey: queryKeys.publicSweepstake(data.slug) });
    },
  });
}
